import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

import DynamicTextInput from "../../components/Inputs/DynamicTextInput";

import {
  createTitle,
  deleteTitle,
  getTitles,
  updateTitle,
} from "../../services/titleService";
import { titleSchema } from "../../validators/titleValidators";

const Title = () => {
  const [titles, setTitles] = useState([]);
  const [filteredTitles, setFilteredTitles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [titleToEdit, setTitleToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [titleToDelete, setTitleToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadTitles();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = titles.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTitles(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, titles]);

  const loadTitles = async () => {
    setLoading(true);
    try {
      const { data } = await getTitles();
      setTitles(data.data);
      setFilteredTitles(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Staff Titles"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (titleToEdit) {
      onUpdateTitle(payload, resetForm);
    } else {
      const nonEmptyTitles = payload?.titles.filter(
        (title) => title?.name !== ""
      );

      if (nonEmptyTitles.length <= 0) {
        toast.error("Staff Titles cannot be empty!");

        return;
      }
      onCreateTitle(nonEmptyTitles, resetForm);
    }
  };

  const onCreateTitle = async (titlesArray, resetForm) => {
    setErrs(null);

    try {
      const promises = titlesArray.map(async (title) => {
        const payload = { name: title.name };

        const { data } = await createTitle(payload);

        return data;
      });

      const createdTitles = await Promise.all(promises);

      setTitles((prev) => [...prev, ...createdTitles]);

      toast.success("Staff Title(s) created successfully");

      setIsModalOpen(false);

      resetForm({ values: { titles: [{ name: "" }] } });

      await loadTitles();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error ||
            "Some Staff Titles were not created, try again!"
        );
      }
    }
  };

  const handleEdit = (accreditingBody) => {
    setTitleToEdit(accreditingBody);
    setIsModalOpen(true);
  };

  const onUpdateTitle = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalTitles = [...titles];

      const titleIndex = originalTitles.findIndex(
        (title) => title.id === titleToEdit.id
      );

      if (titleIndex === -1) throw new Error("Staff Title not found.");

      const { data } = await updateTitle(titleToEdit.id, payload);

      originalTitles[titleIndex] = data.data;

      setTitles(originalTitles);

      setTitleToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Staff Title updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Staff Title not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setTitleToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteTitle = async () => {
    const originalTitles = [...titles];

    const newTitles = originalTitles.filter(
      (title) => title.id !== titleToDelete.id
    );

    setTitles(newTitles);

    try {
      await deleteTitle(titleToDelete?.id);

      toast.success("Staff Title deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Staff Title not deleted, try again later"
        }`
      );

      setTitles(originalTitles);
    } finally {
      setIsDeleteModalOpen(false);

      setTitleToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setTitleToEdit(null);
    setTitleToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 500,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Staff Title">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Staff Title">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Staff Titles"
        subtitle1="Institutions"
        subtitle2="Staff Titles"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Staff Titles List"
            count={filteredTitles.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Staff Title"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredTitles.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {titleToEdit ? "Edit Staff Title" : "Add Staff Title"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            {titleToEdit ? (
              <Formik
                initialValues={{
                  name: titleToEdit ? titleToEdit?.name : "",
                }}
                enableReinitialize
                validationSchema={titleSchema}
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => {
                  return (
                    <Form className="space-y-5 px-4! py-4!">
                      {errs && <FormErrors errors={errs} />}

                      <TextInput
                        name="name"
                        title="Name"
                        placeholder="Enter Staff Title"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />

                      <div className="flex justify-end gap-2">
                        <FormCancelButton
                          text="Cancel"
                          onClick={handleCancel}
                        />
                        <FormButton
                          text="Save"
                          type="primary"
                          onClick={submitForm}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  titles: [{ name: "" }],
                }}
                enableReinitialize
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => (
                  <Form className="space-y-2 !px-5 pb-3">
                    {errs && <FormErrors errors={errs} />}
                    <FieldArray name="titles">
                      {({ remove, push }) =>
                        (values.titles || []).map((accreditingBody, index) => (
                          <DynamicTextInput
                            key={index}
                            index={index}
                            name={`titles.${index}.name`}
                            placeholder="Enter Staff Title Name"
                            requiredField={true}
                            errors={errors}
                            values={values}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            push={push}
                            remove={remove}
                            pushName="titles"
                            dynamicFieldMap={{
                              auditCategories: {
                                name: "",
                              },
                            }}
                          />
                        ))
                      }
                    </FieldArray>
                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteTitle}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Title;
